import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import api from '../api/endpoints';
import {
  TOKEN_KEY, USER_KEY, setUnauthorizedHandler, storage,
} from '../api/client';
import { connectRealtime, disconnectRealtime } from '../services/realtime';

const AuthContext = createContext(null);

/** Which side of the portal the person last chose. See `mode` below. */
const MODE_KEY = 'kw_mode';

const clearSession = () => {
  storage.remove(TOKEN_KEY);
  storage.remove(USER_KEY);
  storage.remove(MODE_KEY);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [partner, setPartner] = useState(null);
  const [booting, setBooting] = useState(true);
  /**
   * Which side of the portal the person is using: 'user' | 'partner'.
   *
   * Deliberately NOT derived from `user.role`. One phone number is one account
   * and the role is a one-way upgrade, so anyone who had ever opened partner
   * signup was sent back to the business form every time they signed in as a
   * customer. The DOOR decides; `role` only says what the account may be.
   */
  const [mode, setModeState] = useState('user');

  const setMode = useCallback((next) => {
    const value = next === 'partner' ? 'partner' : 'user';
    setModeState(value);
    storage.set(MODE_KEY, value);
  }, []);

  /**
   * `intent` is the door the person came through - 'user' from "I need a
   * service", 'partner' from "I provide a service". It wins over the account
   * role, so a partner signing in as a customer gets the customer app.
   */
  const applyAuth = useCallback(async (payload, intent = 'user') => {
    if (payload.token) storage.set(TOKEN_KEY, payload.token);
    if (payload.user) storage.set(USER_KEY, JSON.stringify(payload.user));
    setUser(payload.user || null);
    setPartner(payload.partner || null);
    setMode(intent === 'partner' && payload.user && payload.user.role === 'partner'
      ? 'partner' : 'user');
    // Open the realtime channel as soon as there is a session to authenticate it.
    connectRealtime().catch(() => {});
  }, [setMode]);

  const signOut = useCallback(async () => {
    // Read the token before clearing - the server call below needs it.
    const token = storage.get(TOKEN_KEY);

    // Clear locally FIRST so logout is instant and works offline.
    disconnectRealtime();
    clearSession();
    setUser(null);
    setPartner(null);
    setModeState('user');

    // Best effort: clears the push token server-side. Never blocks the UI.
    if (token) api.auth.logout(token).catch(() => {});
  }, []);

  // Restore the session on first load and refresh it from the server.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setPartner(null);
    });

    (async () => {
      try {
        const token = storage.get(TOKEN_KEY);
        const cached = storage.get(USER_KEY);
        // Restore the side they were last on; only fall back to the role for a
        // session from before this was tracked.
        const savedMode = storage.get(MODE_KEY);
        if (!token) return;
        if (cached) {
          const parsed = JSON.parse(cached);
          setUser(parsed);
          setModeState(savedMode || (parsed.role === 'partner' ? 'partner' : 'user'));
        }
        const res = await api.auth.me();
        setUser(res.data.user);
        setPartner(res.data.partner);
        setModeState(savedMode || (res.data.user.role === 'partner' ? 'partner' : 'user'));
        connectRealtime().catch(() => {});
        storage.set(USER_KEY, JSON.stringify(res.data.user));
      } catch (e) {
        // Only a rejected token means the session is really gone. Every page
        // load on the web re-runs this, so signing out on a timeout, a rate
        // limit or a restarting server would throw people out mid-task - keep
        // the cached user and let the next request settle it.
        if (e.status === 401 || e.status === 403) {
          clearSession();
          setUser(null);
        } else {
          connectRealtime().catch(() => {});
        }
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  const refreshProfile = useCallback(async () => {
    const res = await api.auth.me();
    setUser(res.data.user);
    setPartner(res.data.partner);
    storage.set(USER_KEY, JSON.stringify(res.data.user));
    return res.data;
  }, []);

  const value = useMemo(() => ({
    user,
    partner,
    setPartner,
    setUser,
    booting,
    mode,
    setMode,
    isPartner: Boolean(user && user.role === 'partner'),
    hasBusiness: Boolean(partner),
    applyAuth,
    signOut,
    refreshProfile,
  }), [user, partner, booting, mode, setMode, applyAuth, signOut, refreshProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
